import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { config } from "../Environment";
import useRetryFetch from "../hooks/useRetryFetch";
import { useEffectOnce } from "../hooks/useEffectOnce";
import useAuth from "../hooks/useAuth";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Alert, AlertDescription } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import {
  Check,
  X,
  Mail,
  UserPlus,
  Activity,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  User,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

interface InvitationData {
  _id: string;
  email: string;
  state: string;
  action: string;
  context?: {
    activityName?: string;
    workflowId?: string;
  };
  fiSender?: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  fiUser?: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  created: string;
  expires?: string;
}

function Invitation() {
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();
  const { auth, setAuth } = useAuth();

  const location = useLocation();

  const query = new URLSearchParams(location.search);
  const id = query.get("id");
  // Get email from URL - need to handle the raw search string to preserve '+' characters
  const emailFromQuery = query.get("email");
  let email = emailFromQuery;
  if (emailFromQuery && emailFromQuery.includes(" ")) {
    const rawEmail = location.search.match(/[?&]email=([^&]*)/)?.[1];
    if (rawEmail) {
      email = decodeURIComponent(rawEmail.replace(/\+/g, "%2B"));
    }
  }

  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [notFound, setNotFound] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const isAuthenticated = !!auth?.email;
  const authEmailNormalized = auth?.email?.trim().toLowerCase();
  const invitationEmailNormalized = invitation?.email?.trim().toLowerCase();
  const isMatch =
    isAuthenticated &&
    invitation &&
    authEmailNormalized &&
    invitationEmailNormalized &&
    authEmailNormalized === invitationEmailNormalized;

  // Debug logging for email mismatch detection
  console.log("[Invitation] Email mismatch check:", {
    isAuthenticated,
    authEmail: auth?.email,
    invitationEmail: invitation?.email,
    authEmailNormalized,
    invitationEmailNormalized,
    isMatch,
    invitationState: invitation?.state,
    shouldShowMismatchAlert: isAuthenticated && !isMatch && invitation?.state === "pending",
  });

  async function fetchInvitation() {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (id) params.set("id", id);
      if (email) params.set("email", email);
      const response = await retryFetch(
        `${config.API_BASE}/api/invitations/find?${params.toString()}`
      );
      if (response.ok) {
        const result = await response.json();
        if (result.type === "item") {
          setInvitation(result.item);
          setErrorMessage("");
        }
      } else {
        setErrorMessage(t("invitation_error") || "Failed to load invitation");
        if (response.status === 409) {
          setNotFound(true);
        }
      }
    } catch (err) {
      setErrorMessage(t("invitation_error") || "Failed to load invitation");
      console.log(`[Invitation] error when fetching invitation`);
      console.log(err);
    } finally {
      setLoading(false);
    }
  }

  async function acceptInvitation() {
    if (!id) return;

    try {
      setIsAccepting(true);
      setErrorMessage("");
      const response = await retryFetch(
        `${config.API_BASE}/api/invitations/accept/${encodeURIComponent(id)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (
          result.type === "status" &&
          (result.status === "OK" || result.status === "UPDATED")
        ) {
          setInvitation((prev) =>
            prev ? { ...prev, state: "accepted" } : null
          );
          toast.success(
            t("invitation_accepted") || "Invitation accepted successfully!"
          );
        }
      } else {
        setErrorMessage(
          t("failed_to_accept_invitation") || "Failed to accept invitation"
        );
        toast.error(
          t("failed_to_accept_invitation") || "Failed to accept invitation"
        );
      }
    } catch (err) {
      setErrorMessage(
        t("failed_to_accept_invitation") || "Failed to accept invitation"
      );
      toast.error(
        t("failed_to_accept_invitation") || "Failed to accept invitation"
      );
      console.log(`[Invitation] error when accepting invitation`);
      console.log(err);
    } finally {
      setIsAccepting(false);
    }
  }

  async function rejectInvitation() {
    if (!id) return;

    try {
      setIsRejecting(true);
      setErrorMessage("");
      const response = await retryFetch(
        `${config.API_BASE}/api/invitations/reject/${encodeURIComponent(id)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({}),
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (
          result.type === "status" &&
          (result.status === "OK" || result.status === "UPDATED")
        ) {
          setInvitation((prev) =>
            prev ? { ...prev, state: "rejected" } : null
          );
          toast.success(
            t("invitation_rejected") || "Invitation declined successfully"
          );
        }
      } else {
        setErrorMessage(
          t("failed_to_reject_invitation") || "Failed to decline invitation"
        );
        toast.error(
          t("failed_to_reject_invitation") || "Failed to decline invitation"
        );
      }
    } catch (err) {
      setErrorMessage(
        t("failed_to_reject_invitation") || "Failed to decline invitation"
      );
      toast.error(
        t("failed_to_reject_invitation") || "Failed to decline invitation"
      );
      console.log(`[Invitation] error when rejecting invitation`);
      console.log(err);
    } finally {
      setIsRejecting(false);
    }
  }

  useEffectOnce(() => {
    fetchInvitation();
  }, []);

  function login() {
    navigate("/login", {
      state: { from: location },
    });
  }

  async function loginAsOtherUser() {
    await fetch(config.API_BASE + "/api/auth/logout", {
      credentials: "include",
    });

    setAuth({
      email: undefined,
      accessToken: undefined,
      impersonatedBy: undefined,
    });
    login();
  }

  function getInvitationIcon() {
    if (!invitation) return <Mail className="w-12 h-12 text-gray-400" />;

    switch (invitation.action) {
      case "join_mentor":
        return <UserPlus className="w-12 h-12 text-primary" />;
      case "run_workflow":
        return <Activity className="w-12 h-12 text-purple-600" />;
      default:
        return <Mail className="w-12 h-12 text-gray-600" />;
    }
  }

  function getInvitationTitle() {
    if (!invitation) return t("invitation") || "Invitation";

    switch (invitation.action) {
      case "join_mentor":
        return t("mentor_invitation") || "Mentoring Invitation";
      case "run_workflow":
        return t("activity_invitation") || "Activity Invitation";
      default:
        return t("invitation") || "Invitation";
    }
  }

  function getInvitationDescription() {
    if (!invitation) return "";

    const senderName = invitation.fiSender?.firstName
      ? `${invitation.fiSender.firstName} ${invitation.fiSender.lastName || ""}`
      : invitation.fiSender?.email || t("someone");

    switch (invitation.action) {
      case "join_mentor":
        return (
          t("mentor_invitation_description", { sender: senderName }) ||
          `${senderName} has invited you to connect for coaching or mentoring.`
        );
      case "run_workflow":
        return (
          t("activity_invitation_description", {
            sender: senderName,
            activity: invitation.context?.activityName || "an activity",
          }) ||
          `${senderName} has shared an activity with you: ${invitation.context?.activityName || "an activity"}.`
        );
      default:
        return "";
    }
  }

  function getStatusBadge() {
    if (!invitation) return null;

    switch (invitation.state) {
      case "accepted":
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            {t("accepted") || "Accepted"}
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-100 text-red-800 border-red-200">
            <X className="w-3 h-3 mr-1" />
            {t("declined") || "Declined"}
          </Badge>
        );
      case "pending":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
            <Clock className="w-3 h-3 mr-1" />
            {t("pending") || "Pending"}
          </Badge>
        );
      default:
        return null;
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/20 to-purple-50/20 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-sm text-muted-foreground">
              {t("loading_invitation") || "Loading invitation..."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (errorMessage || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/20 to-purple-50/20 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-red-100 p-3">
                <AlertCircle className="h-12 w-12 text-red-600" />
              </div>
            </div>
            <CardTitle className="text-2xl">
              {notFound
                ? t("invitation_not_found") || "Invitation Not Found"
                : t("error") || "Error"}
            </CardTitle>
            <CardDescription>
              {notFound
                ? t("invitation_not_found_description") ||
                  "This invitation was not found or has expired."
                : t("invitation_error_description") ||
                  "An error occurred while loading the invitation."}
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-center">
            <Button onClick={() => navigate("/portal")}>
              {t("go_to_dashboard") || "Go to Dashboard"}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // If authenticated user is not the intended recipient, show minimal error card
  if (isAuthenticated && !isMatch) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/20 to-purple-50/20 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-red-100 p-3">
                <AlertCircle className="h-12 w-12 text-red-600" />
              </div>
            </div>
            <CardTitle className="text-2xl">
              {t("invitation_not_found") || "Email Mismatch"}
            </CardTitle>
            <CardDescription>
              {t("invitation_email_mismatch", {
                invitationEmail: invitation.email,
                currentEmail: auth.email,
              }) ||
                `This invitation was sent to ${invitation.email} but you are signed in as ${auth.email}. Please sign in as ${invitation.email} to view this invitation.`}
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-center">
            <Button
              size="lg"
              onClick={loginAsOtherUser}
              variant="outline"
              className="w-full"
            >
              <User className="h-4 w-4 mr-2" />
              {t("sign_in_as_different_user") || "Sign In as Different User"}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Show full invitation details only to the intended recipient or unauthenticated users
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/20 to-purple-50/20 p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center mb-2">
            <div className="rounded-full bg-gradient-to-br from-primary/5 to-purple-50 p-4 border border-gray-200">
              {getInvitationIcon()}
            </div>
          </div>
          <div className="flex justify-center">{getStatusBadge()}</div>
          <CardTitle className="text-3xl">{getInvitationTitle()}</CardTitle>
          <CardDescription className="text-base">
            {getInvitationDescription()}
          </CardDescription>
        </CardHeader>

        <Separator />

        <CardContent className="space-y-6 pt-6">
          {/* Sender Information */}
          {invitation.fiSender && (
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-primary/10 p-2">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {t("from") || "From"}
                  </p>
                  <p className="text-sm text-gray-700">
                    {invitation.fiSender.firstName
                      ? `${invitation.fiSender.firstName} ${invitation.fiSender.lastName || ""}`
                      : invitation.fiSender.email}
                  </p>
                  {invitation.fiSender.firstName &&
                    invitation.fiSender.email && (
                      <p className="text-xs text-gray-500">
                        {invitation.fiSender.email}
                      </p>
                    )}
                </div>
              </div>
            </div>
          )}

          {/* Activity Information */}
          {invitation.action === "run_workflow" &&
            invitation.context?.activityName && (
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <div className="flex items-start gap-3">
                  <div className="rounded-full bg-purple-100 p-2">
                    <Activity className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {t("activity") || "Activity"}
                    </p>
                    <p className="text-sm text-gray-700">
                      {invitation.context.activityName}
                    </p>
                  </div>
                </div>
              </div>
            )}

          {/* Recipient Information */}
          <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-primary/10 p-2">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {t("sent_to") || "Sent to"}
                </p>
                <p className="text-sm text-gray-700">{invitation.email}</p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {/* Authentication Status Messages */}
          {!isAuthenticated && invitation.state === "pending" && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t("sign_in_to_accept_invitation") ||
                  "Sign in to accept this invitation."}
              </AlertDescription>
            </Alert>
          )}

          {/* Accepted status - only shown to intended recipient */}
          {invitation.state === "accepted" && isMatch && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {t("invitation_already_accepted") ||
                  "You have accepted this invitation."}
              </AlertDescription>
            </Alert>
          )}

          {/* Rejected status - only shown to intended recipient */}
          {invitation.state === "rejected" && isMatch && (
            <Alert className="border-red-200 bg-red-50">
              <X className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {t("invitation_already_rejected") ||
                  "You have declined this invitation."}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>

        <Separator />

        <CardFooter className="flex flex-col gap-3 pt-6">
          {/* Action Buttons */}
          {!isAuthenticated && invitation.state === "pending" && (
            <div className="w-full space-y-3">
              <Button size="lg" onClick={login} className="w-full">
                <ArrowRight className="h-4 w-4 mr-2" />
                {t("sign_in") || "Sign In"}
              </Button>
              <div className="text-center text-sm text-muted-foreground">
                {t("dont_have_an_account") || "Don't have an account?"}{" "}
                <Link
                  to="/register"
                  state={location.state?.from ? { from: location.state.from } : undefined}
                  className="text-primary hover:underline font-medium"
                >
                  {t("sign_up") || "Sign Up"}
                </Link>
              </div>
            </div>
          )}

          {isAuthenticated && isMatch && invitation.state === "pending" && (
            <div className="w-full flex gap-3">
              <Button
                size="lg"
                onClick={acceptInvitation}
                disabled={isAccepting || isRejecting}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {isAccepting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                {t("accept") || "Accept"}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={rejectInvitation}
                disabled={isAccepting || isRejecting}
                className="flex-1 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                {isRejecting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <X className="h-4 w-4 mr-2" />
                )}
                {t("decline") || "Decline"}
              </Button>
            </div>
          )}

          {invitation.state === "accepted" && (
            <Button size="lg" onClick={() => navigate("/portal")} className="w-full">
              <ArrowRight className="h-4 w-4 mr-2" />
              {t("go_to_dashboard") || "Go to Dashboard"}
            </Button>
          )}

          {invitation.state === "rejected" && (
            <Button
              size="lg"
              onClick={() => navigate("/portal")}
              variant="outline"
              className="w-full"
            >
              {t("go_to_dashboard") || "Go to Dashboard"}
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}

export default Invitation;
